import { useEffect, useRef, useState, useCallback } from 'react'
import Icon from './icons.jsx'
import {
  buildWhatsAppUrl,
  buildMailtoUrl,
  SUPPORT_WHATSAPP_NUMBER,
  SUPPORT_EMAIL,
} from '../lib/supportConfig.js'

/** Duração da animação de saída — precisa bater com .sup-overlay.is-closing no CSS. */
const CLOSE_MS = 200

/** Um card de canal de contato (WhatsApp / E-mail). */
function ChannelCard({ icon, title, desc, accent, href, target, ariaLabel }) {
  return (
    <a
      className="sup-card"
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      aria-label={ariaLabel}
      style={{ '--sup-accent': accent }}
    >
      <span className="sup-card-icon">
        <Icon n={icon} s={22} />
      </span>
      <span className="sup-card-text">
        <span className="sup-card-title">{title}</span>
        <span className="sup-card-desc">{desc}</span>
      </span>
      <span className="sup-card-arrow" aria-hidden="true">
        <Icon n="chevron_r" s={16} />
      </span>
    </a>
  )
}

/**
 * Modal de Suporte centralizado.
 *
 * Comportamento:
 *  - Animação suave de entrada/saída (estado `closing`).
 *  - Fecha no botão "X", no clique fora ou na tecla Esc.
 *  - Foco move para o botão de fechar ao abrir e retorna ao gatilho ao fechar.
 */
export default function SupportModal({ open, onClose }) {
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const closeBtnRef = useRef(null)
  const triggerRef = useRef(null)

  // Inicia a sequência de fechamento com animação antes de desmontar.
  const startClose = useCallback(() => {
    setClosing(true)
    const t = setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
    return () => clearTimeout(t)
  }, [onClose])

  // Sincroniza montagem com a prop `open` (montar imediato, desmontar animado).
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement
      setMounted(true)
      setClosing(false)
    } else if (mounted) {
      startClose()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Foco inicial + retorno de foco + trava de scroll do body enquanto aberto.
  useEffect(() => {
    if (!mounted) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeBtnRef.current?.focus()
    return () => {
      document.body.style.overflow = prevOverflow
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus()
    }
  }, [mounted])

  // Esc fecha o modal.
  useEffect(() => {
    if (!mounted) return
    function onKey(e) {
      if (e.key === 'Escape') startClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay ${closing ? 'is-closing' : ''}`}
      onClick={startClose}
    >
      <div
        className="sup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sup-title"
        aria-describedby="sup-subtitle"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          className="sup-close"
          onClick={startClose}
          aria-label="Fechar suporte"
        >
          <Icon n="x" s={18} />
        </button>

        <div className="sup-head">
          <h2 id="sup-title" className="sup-title">Suporte</h2>
          <p id="sup-subtitle" className="sup-subtitle">
            Encontrou um problema ou tem uma sugestão? Fale com a gente.
          </p>
        </div>

        <div className="sup-cards">
          <ChannelCard
            icon="whats"
            title="WhatsApp"
            desc="Atendimento rápido pelo chat"
            accent="#22c55e"
            href={buildWhatsAppUrl()}
            target="_blank"
            ariaLabel={`Falar no WhatsApp ${SUPPORT_WHATSAPP_NUMBER}`}
          />
          <ChannelCard
            icon="mail"
            title="E-mail"
            desc="Envie sua dúvida ou sugestão"
            accent="var(--blue)"
            href={buildMailtoUrl()}
            ariaLabel={`Enviar e-mail para ${SUPPORT_EMAIL}`}
          />
        </div>
      </div>
    </div>
  )
}
